import { useState } from "react"
import pluralize from "pluralize"

import type {
    EventOrganizerTypeForContext,
    OutfitterTypeForContext,
    PortalTypeForContext,
} from "~contexts/outfitter-provider"
import { usePortalEntitiesContext } from "~contexts/outfitter-provider"
import { PortalType } from "~graphql/generated/types"
import { useUser } from "~contexts/user-provider"
import { getCleanEnum } from "~utils/helpers/string-helpers"
import { useDebounceCallback } from "~utils/hooks/use-debounce"
import COPY from "~config/copy-constants"

export function useAdminSearchProps() {
    const { isAdminNotImpersonating } = useUser()
    const { portalType, adminSearchText, setAdminSearchText, isLoading } =
        usePortalEntitiesContext()
    const searchPlaceholder = getSearchPlaceholder({ portalType })

    const [localSearchText, setLocalSearchText] = useState(adminSearchText)

    useDebounceCallback({
        value: localSearchText,
        callBack: setAdminSearchText,
    })

    if (!isAdminNotImpersonating) return undefined

    return {
        isSearching: isLoading,
        searchText: localSearchText,
        onSearchTextChange: setLocalSearchText,
        placeholder: searchPlaceholder,
    }
}

export function getSelectPlaceholder({
    portalType,
}: {
    portalType: PortalTypeForContext
}) {
    return `${COPY.SELECT_AN} ${getCleanEnum(portalType)}`
}

function getSearchPlaceholder({
    portalType,
}: {
    portalType: PortalTypeForContext
}) {
    const pluralPortalType = pluralize(getCleanEnum(portalType), 2)

    return `${COPY.SEARCH_ALL} ${pluralPortalType}`
}

export function useSelectedEntity({
    portalType,
    selectedOutfitter,
    selectedEventOrganizer,
}: {
    portalType: PortalTypeForContext
    selectedOutfitter: OutfitterTypeForContext
    selectedEventOrganizer: EventOrganizerTypeForContext
}): SelectedEntity | null {
    const { isAdminNotImpersonating } = useUser()

    switch (portalType) {
        case PortalType.RegionalDirector:
            return getRegionalDirectorSelectedEntity({
                selectedEventOrganizer,
                isAdminNotImpersonating,
            })
        case PortalType.Outfitter:
            return getOutfitterSelectedEntity({
                selectedOutfitter,
                isAdminNotImpersonating,
            })
        default:
            return null
    }
}

function getRegionalDirectorSelectedEntity({
    selectedEventOrganizer,
    isAdminNotImpersonating,
}: {
    selectedEventOrganizer: EventOrganizerTypeForContext
    isAdminNotImpersonating: boolean
}) {
    if (!isAdminNotImpersonating && !selectedEventOrganizer) return null

    return {
        name: selectedEventOrganizer?.name ?? COPY.ALL_REGIONAL_DIRECTORS,
        isAllSelectedEntity: !selectedEventOrganizer,
    }
}

function getOutfitterSelectedEntity({
    selectedOutfitter,
    isAdminNotImpersonating,
}: {
    selectedOutfitter: OutfitterTypeForContext
    isAdminNotImpersonating: boolean
}) {
    if (!isAdminNotImpersonating && !selectedOutfitter) return null

    return {
        avatar: selectedOutfitter?.avatar?.url,
        name: selectedOutfitter?.name ?? COPY.ALL_OUTFITTERS,
        isAllSelectedEntity: !selectedOutfitter,
    }
}

export interface SelectedEntity {
    avatar?: string
    name: string
    isAllSelectedEntity: boolean
}
